import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChartBarIcon,
  BuildingIcon,
  GlobeAltIcon,
  UserGroupIcon,
  UsersIcon,
  CurrencyDollarIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  TagIcon,
} from './Icons';

const navItems = [
  { id: 'dashboard', name: 'Overview', href: '/platform/admin/dashboard', icon: <ChartBarIcon /> },
  { id: 'plans', name: 'Plans & Pricing', href: '/platform/admin/plans', icon: <TagIcon /> },
  { id: 'integrations', name: 'Integrations', href: '/platform/admin/integrations', icon: <TagIcon /> },
  { id: 'tenants', name: 'Tenants', href: '/platform/admin/tenants', icon: <BuildingIcon /> },
  { id: 'users', name: 'Users', href: '/platform/admin/users', icon: <UsersIcon /> },
  { id: 'websites', name: 'Websites', href: '/platform/admin/websites', icon: <GlobeAltIcon /> },
  { id: 'leads', name: 'Leads', href: '/platform/admin/leads', icon: <UserGroupIcon /> },
  { id: 'revenue', name: 'Revenue', href: '/platform/admin/revenue', icon: <CurrencyDollarIcon /> },
  { id: 'health', name: 'System Health', href: '/platform/admin/health', icon: <HeartIcon /> },
  { id: 'audit', name: 'Audit Logs', href: '/platform/admin/audit-logs', icon: <ClipboardDocumentListIcon /> },
];

const PlatformAdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = () => {
    logout();
    navigate('/platform/admin/login');
  };

  return (
    <div className="min-h-screen bg-sidebar-bg">
      {/* Sidebar - Mamba style */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-bg border-r border-sidebar-border transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-fg font-bold text-sm">PA</span>
              </div>
              <span className="font-semibold text-sidebar-fg">Platform Admin</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded text-sidebar-fg-muted hover:text-sidebar-fg"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex-1 space-y-0.5 px-3 py-4 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-sidebar-active text-sidebar-fg border-l-2 border-primary -ml-0.5 pl-3.5'
                      : 'text-sidebar-fg-muted hover:bg-sidebar-hover hover:text-sidebar-fg'
                  }`}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-sidebar-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center text-primary-fg font-medium">
                {user?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-fg truncate">{user?.email}</p>
                <p className="text-xs text-sidebar-fg-muted">Superuser</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 text-sm text-sidebar-fg-muted hover:text-sidebar-fg hover:bg-sidebar-hover rounded-lg transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center border-b border-sidebar-border bg-sidebar-bg/95 backdrop-blur px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 text-sidebar-fg-muted hover:text-sidebar-fg"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </header>
        <main className="p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PlatformAdminLayout;
